import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useEffect, useState } from "react";
import errorimg from "../../assets/used-icons/error.png";
import { ArrowLeft, ArrowRight, Filter } from "lucide-react";
import Breadcrumb from "./Breadcrumb";
import TrainingCourseBox from "../TrainingCourses/content/TrainingCourseBox";
import FilterContent from "../TrainingCourses/content/FilterContent";
import TrainingCourseLoading from "../loading/taining-course-box";
import { useLanguage } from "../../store/language-context";
import { useTranslation } from "react-i18next";

const fetchCourses = async (url) => {
    const response = await axios.get(url);
    return response?.data?.data;
};

const TrainingCourses = () => {
    const { URL, lang } = useLanguage();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const searchedKey = searchParams.get("title");
    const pageNumber = searchParams.get("page") || 1;

    const urlCategory = searchParams.get('category_id');
    const urlCity = searchParams.get('city_id');
    const urlDependence = searchParams.get('dependence_id');
    const urlStartDate = searchParams.get('start_date_week');
    const urlEndDate = searchParams.get('end_date_week');
    const urlSubject = searchParams.get('subject');

    const [seeFilter, setSeeFilter] = useState(false);
    const [date, setDate] = useState({ start: urlStartDate || "", end: urlEndDate || "" });
    const [selected, setSelected] = useState({
        title: searchedKey || "",
        city: urlCity || "",
        category: urlCategory || "",
        subject: urlSubject || "",
        dependence: urlDependence || "",
    });

    const [currentPage, setCurrentPage] = useState(pageNumber);
    const [apiUrl, setApiUrl] = useState(`${URL}/courses?page=${currentPage}&per_page=9`);

    useEffect(() => {
        if (URL || lang) {
            setApiUrl(`${URL}/courses?page=${currentPage}&per_page=9`);
        }
    }, [URL, lang]);

    const { data, error, isLoading } = useQuery({
        queryKey: ['courses', apiUrl, searchedKey, lang],
        queryFn: () => fetchCourses(searchedKey ? `${apiUrl}&title=${searchedKey}` : apiUrl),
        retry: false,
        refetchOnWindowFocus: false,
    });

    const courses = data?.data || [];
    const meta = data?.meta || {};
    const links = data?.links || {};
    const lastPage = meta?.last_page || 1;
    const prevPageUrl = links?.prev;
    const nextPageUrl = links?.next;

    const filteringAndPagination = (page = null) => {
        let url = `${URL}/courses?per_page=9`;

        if (page) url += `&page=${page}`;
        else url += `&page=${currentPage}`;

        if (searchedKey) url += `&title=${searchedKey}`;
        if (selected.title) url += `&title=${selected.title}`;
        if (selected.dependence) url += `&dependence_id=${selected.dependence}`;
        if (selected.city) url += `&city_id=${selected.city}`;
        if (selected.subject) url += `&subject=${selected.subject}`;
        if (selected.category) url += `&category_id=${selected.category}`;
        if (date.start) url += `&start_date_week=${date.start}`;
        if (date.end) url += `&end_date_week=${date.end}`;

        setApiUrl(url);

        if (page) {
            setCurrentPage(page);
        }
        navigate(`/training-courses?page=${page || 1}&title=${decodeURIComponent(selected.title)}&dependence_id=${selected.dependence}&city_id=${selected.city}&subject=${selected.subject}&category_id=${selected.category}&start_date_week=${date.start}&end_date_week=${date.end}`);
    };

    const resetFilter = () => {
        setApiUrl(`${URL}/courses?page=${pageNumber || currentPage}`);
        setDate({ start: "", end: "" });
        setSelected({
            title: "",
            city: "",
            category: "",
            subject: "",
            dependence: "",
        });
        setSeeFilter(true);
        navigate(`/training-courses?page=1`)
    };

    const paginationNumbers = () => {
        const numbers = [];
        for (let i = 1; i <= lastPage; i++) {
            if (
                i <= 5 ||
                i === currentPage ||
                i === lastPage ||
                (i === currentPage - 1 && currentPage > 3) ||
                (i === currentPage + 1 && currentPage < lastPage)
            ) {
                numbers.push(i);
            }
        }

        const paginationWithDots = [];
        for (let i = 0; i < numbers.length; i++) {
            paginationWithDots.push(numbers[i]);
            if (i < numbers.length - 1 && numbers[i + 1] - numbers[i] > 1) {
                paginationWithDots.push("...");
            }
        }
        return paginationWithDots;
    };

    useEffect(() => {
        if (urlCategory) {
            filteringAndPagination(null)
        }
    }, [urlCategory]);

    return (
        <>
            <Breadcrumb pages={[t('routes.training-courses')]} />

            <main className="container max-lg:!px-6">
                <section className="text-start">
                    {/* Filter Button */}
                    <button
                        className={`filter-btn ${seeFilter ? "opacity-0 max-h-0 max-w-0 " : "opacity-100 max-h-full w-auto mb-5"}`}
                        onClick={() => setSeeFilter((prev) => !prev)}
                    >
                        <span>
                            <Filter size={24} color="white" />
                        </span>
                        <span>{t('training-courses.filter')}</span>
                    </button>

                    <section className="no-pm flex gap-4 items-start max-lg:flex-col">
                        {/* Filter Content */}
                        <div
                            className={`${seeFilter ? "opacity-100 max-h-full w-[32rem] max-lg:w-full" : "opacity-0 max-h-0 w-0"} transition-all delay-0 ease-in-out`}
                        >
                            <FilterContent
                                setSeeFilter={setSeeFilter}
                                seeFilter={seeFilter}
                                date={date}
                                setDate={setDate}
                                setSelected={setSelected}
                                selected={selected}
                            >
                                <div className="flex justify-center items-center gap-3">
                                    <button className="filter-btn" onClick={() => filteringAndPagination(null)}>
                                        {t('custom.search')}
                                    </button>
                                    <button className="filter-btn n" onClick={resetFilter}>
                                        {t('custom.reset')}
                                    </button>
                                </div>
                            </FilterContent>
                        </div>

                        {/* Courses Grid */}
                        <section className={`grid ${seeFilter ? `${error ? 'grid-cols-1' : 'grid-cols-2'} gap-14 max-2xl:grid-cols-1` : `${error ? 'grid-cols-1' : 'grid-cols-3'} gap-14 max-2xl:grid-cols-2 max-lg:gap-14`} py-8 px-1 max-md:grid-cols-1 w-full`}>
                            {isLoading && !error ? (
                                <TrainingCourseLoading />
                            ) : error || courses.length <= 0 ? (
                                <div className="flex justify-center items-center h-full w-full text-xl flex-column">
                                    <p id="e"><img src={errorimg} alt="" className="img-fluid w-75 m-auto" /></p>
                                    <p id="err">{t('training-courses.no-data')}</p>
                                </div>
                            ) : (
                                courses?.map((course) => (
                                    <TrainingCourseBox key={course.id} course={course} selectedCity={selected?.city} />
                                ))
                            )}
                        </section>
                    </section>

                    {/* Pagination Controls */}
                    {lastPage > 1 && (
                        <div className="flex gap-3 justify-center items-center my-6 flex-wrap">
                            <button
                                className="border-secondary-tw border-2 px-2 py-1 rounded-md hover:ring-1 hover:ring-yellow-200"
                                disabled={!prevPageUrl}
                                onClick={() => filteringAndPagination(currentPage - 1)}
                            >
                                <ArrowRight size={20} color="#e08b3d" fill="#e08b3d" className="ltr:rotate-180"/>
                            </button>
                            {paginationNumbers().map((num, index) => (
                                <button
                                    key={index}
                                    className={`mx-1 text-base ${num.toString() === currentPage.toString() ? "text-secondary-tw border-secondary-tw border-2 px-2" : "hover:text-secondary"} rounded-md`}
                                    onClick={() => num !== "..." && filteringAndPagination(num)}
                                    disabled={num === "..."}

                                >
                                    {num}
                                </button>
                            ))}
                            <button
                                className="border-secondary-tw border-2 px-2 py-1 rounded-md hover:ring-1 hover:ring-yellow-200"
                                disabled={!nextPageUrl}
                                onClick={() => filteringAndPagination(currentPage + 1)}
                            >
                                <ArrowLeft size={20} color="#e08b3d" fill="#e08b3d" className="ltr:rotate-180" />
                            </button>
                        </div>
                    )}

                </section>
            </main>
        </>
    );
};

export default TrainingCourses;